// hooks/usePublishedHeight.js
// ─────────────────────────────────────────────────────────────────────────────
// Publish an element's rendered height as a CSS custom property on <html>, so
// the rest of the page can lay out against it without knowing it.
//
// The fixed Navbar is the original case: on a phone it is three stacked rows,
// not the 121px it is on a desktop, and every page that hardcoded 121 put its
// sticky section strip straight into the search field. The strip itself is
// the second case: the scroll-margin under it has to clear BOTH bars, and only
// the browser knows how tall they came out.
//
// A ResizeObserver keeps the value live through orientation changes, font
// loading and the location row wrapping; the window resize listener is the
// fallback for browsers without one. On unmount the property is removed, so a
// page whose bar is gone falls back to the `var(--x, fallback)` it declared.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from "react";

const usePublishedHeight = (ref, cssVar) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !cssVar) return undefined;
    const publish = () =>
      document.documentElement.style.setProperty(cssVar, `${el.offsetHeight}px`);
    publish();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(publish);
      ro.observe(el);
    }
    window.addEventListener("resize", publish);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", publish);
      document.documentElement.style.removeProperty(cssVar);
    };
  }, [ref, cssVar]);
};

export default usePublishedHeight;
